/**
 * Admin · PWS Partner Overview (Phase 1, A139)
 *
 * Single admin overview screen for each PWS Partner. One card per
 * partner with seven phase-readiness chips + a last-activity summary
 * so admins can see where each franchisee is up to without digging
 * through multiple admin screens.
 *
 * Read-only. No mutations from this page.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Search, Loader2, AlertTriangle, ExternalLink, RefreshCw,
  ArrowLeft, ArrowRight, CheckCircle2, Circle, AlertCircle, Ban, Wrench, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { formatDateAU } from "@/lib/formatDateAU";

const CHIP_STYLES = {
  not_started:    { icon: Circle,        bg: "bg-slate-50",   text: "text-slate-500",   border: "border-slate-200",   label: "Not started" },
  in_progress:    { icon: AlertCircle,   bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200",   label: "In progress" },
  ready:          { icon: CheckCircle2,  bg: "bg-cyan-50",    text: "text-cyan-700",    border: "border-cyan-200",    label: "Ready" },
  complete:       { icon: CheckCircle2,  bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", label: "Complete" },
  blocked:        { icon: Ban,           bg: "bg-rose-50",    text: "text-rose-700",    border: "border-rose-200",    label: "Blocked" },
  // Feb 2026 — Option A: green "N/A" chip. Rendered when a pillar's
  // feature flag is off (auto-N/A) OR an admin has explicitly marked
  // the pillar not_applicable for this partner.
  not_applicable: { icon: CheckCircle2,  bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", label: "N/A" },
};

function Chip({ status, small }) {
  const style = CHIP_STYLES[status] || CHIP_STYLES.not_started;
  const Icon = style.icon;
  return (
    <span
      data-testid={`chip-${status}`}
      className={`inline-flex items-center gap-1 rounded-full border ${style.bg} ${style.text} ${style.border} ${small ? "px-1.5 py-[1px] text-[9px]" : "px-2 py-0.5 text-[10px]"} font-bold uppercase tracking-wider`}
    >
      <Icon className={small ? "w-2.5 h-2.5" : "w-3 h-3"} />
      {style.label}
    </span>
  );
}

function ProgressBar({ pct }) {
  const color = pct >= 100
    ? "bg-emerald-500"
    : pct >= 60
      ? "bg-cyan-500"
      : pct > 0
        ? "bg-amber-500"
        : "bg-slate-300";
  return (
    <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
      <div className={`h-full ${color} transition-all`} style={{ width: `${Math.max(2, pct)}%` }} />
    </div>
  );
}

export default function PWSPartnerOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [rawQ, setRawQ] = useState("");
  const [repairOpen, setRepairOpen] = useState(false);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setQ(rawQ.trim()), 300);
    return () => clearTimeout(t);
  }, [rawQ]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/pws-partner-overview", { params: q ? { q } : {} });
      setData(res.data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  // Feb 2026 — cross-tab / cross-page refresh. Two triggers:
  //   1. `pws-partner-updated` event — fired by the PWS Partner
  //      drawer (network admin) whenever an admin saves ANY
  //      rollout-setup form (branding, commission, sample, SLA,
  //      pickup/dropoff, fulfilment, slug). Ensures this page
  //      re-fetches even when it's the parent tab the admin came
  //      from and is still mounted.
  //   2. `visibilitychange` — when the admin switches BACK to this
  //      tab from another tab where they edited a partner, refresh
  //      so stale chip data doesn't linger.
  useEffect(() => {
    const refetch = () => load();
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("pws-partner-updated", refetch);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("pws-partner-updated", refetch);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  const items = data?.items || [];
  const summary = data?.summary || { total: 0, avg_progress: 0, fully_ready: 0, not_started: 0 };
  const phaseLabels = data?.phase_labels || {};
  const phaseKeys = data?.phase_keys || [];

  const sortedItems = useMemo(() => {
    // Highest progress first, tie-break by business name
    return [...items].sort((a, b) => (b.progress_pct - a.progress_pct)
      || String(a.business_name || "").localeCompare(String(b.business_name || "")));
  }, [items]);

  return (
    <PortalShell>
      <div className="max-w-7xl mx-auto p-6" data-testid="pws-partner-overview-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div>
            <Link
              to="/admin/partners"
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-2"
            >
              <ArrowLeft className="w-3 h-3" /> Back to Partners
            </Link>
            <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="pws-partner-overview-h1">
              PWS Partner Overview
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Read-only rollout snapshot across the 7 core phases · Phase 1.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="pws-partner-overview-refresh"
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900"
              title="Refresh"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Refresh
            </button>
            <button
              onClick={() => setRepairOpen(true)}
              data-testid="pws-partner-overview-repair-names"
              className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-900 border border-cyan-200 bg-cyan-50 hover:bg-cyan-100 px-2.5 py-1 rounded-full"
              title="Heal partners with blank business names by pulling from Business Details or landing page."
            >
              <Wrench className="w-3.5 h-3.5" />
              Repair blank names
            </button>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="pws-partner-overview-summary">
          <SummaryTile testid="summary-total"       label="Total PWS partners" value={summary.total} accent="cyan" icon={Users} />
          <SummaryTile testid="summary-avg"         label="Avg progress"       value={`${summary.avg_progress}%`} accent="amber" />
          <SummaryTile testid="summary-fully-ready" label="Fully ready"        value={summary.fully_ready} accent="emerald" icon={CheckCircle2} />
          <SummaryTile testid="summary-not-started" label="Not started"        value={summary.not_started} accent="slate" icon={Circle} />
        </div>

        {/* Search */}
        <div className="mb-4 flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={rawQ}
              onChange={(e) => setRawQ(e.target.value)}
              placeholder="Search by business name, region, state, slug or contact…"
              data-testid="pws-partner-overview-search"
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200"
            />
          </div>
        </div>

        {/* Content */}
        {error ? (
          <div data-testid="pws-partner-overview-error" className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" /> {error}
          </div>
        ) : loading && !data ? (
          <div className="text-slate-400 flex items-center gap-2 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading partner overview…</div>
        ) : data?.is_empty ? (
          <div data-testid="pws-partner-overview-empty" className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center">
            <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <div className="font-display font-semibold text-slate-900">No PWS partners yet</div>
            <div className="text-xs text-slate-500 mt-1">Franchisee rows will appear here once they are imported or manually added via the network admin.</div>
          </div>
        ) : sortedItems.length === 0 ? (
          <div data-testid="pws-partner-overview-no-match" className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
            No partners match “{q}”.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="pws-partner-overview-cards">
            {sortedItems.map((row) => (
              <PartnerCard key={row.partner_id} row={row} phaseKeys={phaseKeys} phaseLabels={phaseLabels} />
            ))}
          </div>
        )}
      </div>
      {repairOpen && (
        <RepairBlankNamesModal
          onClose={() => setRepairOpen(false)}
          onApplied={() => { setRepairOpen(false); load(); }}
        />
      )}
    </PortalShell>
  );
}

function SummaryTile({ testid, label, value, accent, icon: Icon }) {
  const t = {
    cyan:    "text-cyan-700 bg-cyan-50 border-cyan-200",
    amber:   "text-amber-700 bg-amber-50 border-amber-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    slate:   "text-slate-600 bg-slate-50 border-slate-200",
  }[accent] || "text-slate-600 bg-slate-50 border-slate-200";
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4">
      {Icon && (
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center mb-1.5 ${t}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
      )}
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function PartnerCard({ row, phaseKeys, phaseLabels }) {
  const activity = row.activity || {};
  const [pendingOverride, setPendingOverride] = useState(null);   // pillar key being toggled
  // Feb 2026 — deep-link each rollout chip to the admin surface that
  // owns the underlying config. Most chips live on the partner drawer
  // (opened by `?open=<id>` on /admin/partners) — the hash tells the
  // drawer's RolloutSetupPanel which accordion to auto-expand.
  const openDrawer = `/admin/partners/network?open=${encodeURIComponent(row.partner_id)}`;
  const configLink = {
    profile:         `${openDrawer}#business-details`,
    pricing:         "/admin/pricing/test-catalog",
    commission:      `${openDrawer}#rollout-commission`,
    branding:        `${openDrawer}#rollout-branding`,
    booking:         `${openDrawer}#rollout-booking`,
    sla:             `${openDrawer}#rollout-sla`,
    report_sla:      `${openDrawer}#rollout-sla`,
    marketing_kit:   `/admin/partners/marketing-kit?partner_id=${encodeURIComponent(row.partner_id)}`,
    sample_workflow: `${openDrawer}#rollout-sample`,
  };
  // Feb 2026 — "Fix Missing Pieces" button. Aggregates every
  // not-started / in-progress pillar into a single deep link with
  // `#rollout-fix` — the drawer's RolloutSetupPanel expands every
  // pillar in that list so admins can knock them off top-to-bottom.
  const missingKeys = phaseKeys.filter((k) => {
    const s = (row.chips?.[k] || {}).status;
    return s === "not_started" || s === "in_progress";
  });
  const bulkFixHref = missingKeys.length > 0
    ? `${openDrawer}#rollout-fix=${missingKeys.join(",")}`
    : null;
  return (
    <div
      data-testid={`pws-partner-card-${row.partner_id}`}
      className="rounded-3xl border border-slate-200 bg-white p-5 flex flex-col gap-4"
    >
      {/* Card header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg font-bold text-slate-900 truncate">
              {row.business_name || "(unnamed)"}
            </div>
            <span data-testid={`pws-partner-status-${row.partner_id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold text-slate-600 uppercase tracking-wider">
              {row.pws_partner_status || "draft"}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {row.franchise_region || "—"}
            {row.state ? ` · ${row.state}` : ""}
            {row.partner_slug ? ` · ${row.partner_slug}` : ""}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {row.contact_name || "—"}
            {row.email ? ` · ${row.email}` : ""}
            {row.phone ? ` · ${row.phone}` : ""}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <Link
            to={`/admin/partners/network?open=${encodeURIComponent(row.partner_id)}`}
            data-testid={`pws-partner-open-${row.partner_id}`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-900"
            title="Open in Partners admin"
          >
            Open <ExternalLink className="w-3 h-3" />
          </Link>
          {bulkFixHref && (
            <Link
              to={bulkFixHref}
              data-testid={`pws-partner-bulk-fix-${row.partner_id}`}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5 transition-colors"
              title={`Expand ${missingKeys.length} incomplete pillar${missingKeys.length === 1 ? "" : "s"} in the drawer`}
            >
              Fix {missingKeys.length} piece{missingKeys.length === 1 ? "" : "s"} <ArrowRight className="w-2.5 h-2.5" />
            </Link>
          )}
        </div>
      </div>

      {/* Progress */}
      <div>
        <div className="flex items-center justify-between text-[11px] mb-1">
          <span className="text-slate-500">Rollout progress</span>
          <span className="font-semibold text-slate-800 tabular-nums" data-testid={`pws-partner-progress-${row.partner_id}`}>
            {row.progress_pct}%
          </span>
        </div>
        <ProgressBar pct={row.progress_pct} />
      </div>

      {/* Phase chips grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`pws-partner-chips-${row.partner_id}`}>
        {phaseKeys.map((key) => {
          const chip = row.chips[key] || { status: "not_started", summary: "—" };
          const href = configLink[key] || openDrawer;
          const isOverride = !!chip.override;
          const isNA = chip.status === "not_applicable";
          const toggleNA = async (e) => {
            e.preventDefault(); e.stopPropagation();
            if (pendingOverride) return;
            setPendingOverride(key);
            try {
              await api.post(`/admin/pws-partners/${row.partner_id}/pillar-override`, {
                pillar:   key,
                // Toggle: if the pillar is already explicitly overridden,
                // clear it (revert to computed chip). Otherwise mark N/A.
                override: isOverride ? null : "not_applicable",
              });
              // Dispatch the same event the drawer uses so the Overview
              // page refetches without a manual reload.
              window.dispatchEvent(new CustomEvent("pws-partner-updated", {
                detail: { partner_id: row.partner_id },
              }));
              toast.success(
                isOverride
                  ? `Reverted ${phaseLabels[key] || key} — chip is auto-computed again.`
                  : `Marked ${phaseLabels[key] || key} as N/A.`,
              );
            } catch (err) {
              toast.error(formatApiError(err, "Toggle failed"));
            } finally { setPendingOverride(null); }
          };
          return (
            <div key={key} className="relative group">
              <Link
                to={href}
                data-testid={`pws-phase-${row.partner_id}-${key}`}
                className="flex items-start justify-between gap-2 rounded-xl border border-slate-100 bg-slate-50/50 hover:bg-cyan-50/60 hover:border-cyan-200 p-2.5 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1">
                    {phaseLabels[key] || key}
                    {isOverride && (
                      <span
                        title="Marked N/A by admin"
                        className="inline-block w-1 h-1 rounded-full bg-emerald-500"
                      />
                    )}
                  </div>
                  <div className="text-[11px] text-slate-700 mt-0.5 truncate" title={chip.summary}>
                    {chip.summary}
                  </div>
                  <div className="mt-1 text-[10px] font-semibold text-cyan-700 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                    Configure <ExternalLink className="w-2.5 h-2.5" />
                  </div>
                </div>
                <Chip status={chip.status} small />
              </Link>
              {/* Feb 2026 — Option A: admin can flip any pillar to a
                  green "N/A" chip for THIS partner. Auto-N/A already
                  fires when a feature flag is off; this toggle covers
                  pillars without a flag (marketing kit, profile, etc.)
                  and lets admin re-override the auto behaviour. */}
              <button
                type="button"
                data-testid={`pws-phase-mark-na-${row.partner_id}-${key}`}
                disabled={pendingOverride === key}
                onClick={toggleNA}
                title={isOverride ? "Revert (auto-compute chip again)" : "Mark as N/A for this partner"}
                className={`absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                  isOverride
                    ? "bg-slate-700 text-white hover:bg-slate-900"
                    : (isNA ? "bg-slate-200 text-slate-700 hover:bg-slate-300"
                            : "bg-emerald-600 text-white hover:bg-emerald-700")
                } disabled:opacity-50`}
              >
                {isOverride ? "Revert" : (isNA ? "N/A ✓" : "Mark N/A")}
              </button>
            </div>
          );
        })}
      </div>

      {/* Activity footer */}
      <div
        data-testid={`pws-partner-activity-${row.partner_id}`}
        className="border-t border-slate-100 pt-3 grid grid-cols-3 gap-2 text-[10px]"
      >
        <ActivityCell label="Last engaged" value={activity.last_engaged_at ? formatDateAU(activity.last_engaged_at) : "—"} />
        <ActivityCell label="Last login"   value={activity.last_login_at   ? formatDateAU(activity.last_login_at)   : "—"} />
        <ActivityCell label="Activated"    value={activity.activated_at    ? formatDateAU(activity.activated_at)    : "—"} />
      </div>
    </div>
  );
}

function ActivityCell({ label, value }) {
  return (
    <div>
      <div className="uppercase tracking-wider font-bold text-slate-400">{label}</div>
      <div className="text-slate-700 mt-0.5">{value}</div>
    </div>
  );
}

/**
 * Repair blank business names — admin housekeeping modal (Feb 2026).
 *
 * Opens on the PWS Partner Overview page. Auto-runs a server dry-run
 * on mount, previews the fallback resolutions (source per row), then
 * lets the admin apply. Backend is fully non-destructive — refuses to
 * overwrite any row whose `business_name` is already non-blank.
 */
function RepairBlankNamesModal({ onClose, onApplied }) {
  const [phase, setPhase] = useState("loading"); // loading | preview | applying | done | error
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const runDryRun = useCallback(async () => {
    setPhase("loading");
    setError("");
    try {
      const res = await api.post(
        "/admin/pws-partners/backfill-business-names?dry_run=true",
      );
      setPreview(res.data);
      setPhase("preview");
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail || e.message));
      setPhase("error");
    }
  }, []);

  useEffect(() => { runDryRun(); }, [runDryRun]);

  const apply = async () => {
    setPhase("applying");
    setError("");
    try {
      const res = await api.post(
        "/admin/pws-partners/backfill-business-names",
      );
      const n = res?.data?.updated ?? 0;
      toast.success(n === 0
        ? "Nothing to repair — every partner already has a business name."
        : `Repaired ${n} partner${n === 1 ? "" : "s"}.`);
      onApplied?.();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail || e.message));
      setPhase("error");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
      data-testid="pws-repair-names-modal"
    >
      <div className="bg-white rounded-3xl max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-cyan-600" />
              Repair blank business names
            </h2>
            <p className="text-xs text-slate-500 mt-1 max-w-md">
              Fills a partner&rsquo;s blank <code className="text-[10px]">business_name</code> from
              their Business Details tab, then from their landing page. Never overwrites an
              existing name.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="pws-repair-names-close"
            className="p-1.5 hover:bg-slate-100 rounded-lg"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {phase === "loading" && (
          <div className="text-slate-400 flex items-center gap-2 py-6" data-testid="pws-repair-names-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Scanning partners…
          </div>
        )}

        {phase === "error" && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 flex items-start gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">Repair failed</div>
              <div className="text-xs mt-0.5">{error}</div>
            </div>
            <button
              onClick={runDryRun}
              className="text-xs font-semibold underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        {(phase === "preview" || phase === "applying") && preview && (
          <>
            <div className="grid grid-cols-3 gap-2 mb-4" data-testid="pws-repair-names-summary">
              <SummaryTile testid="repair-scanned" label="Scanned"    value={preview.scanned} accent="slate" />
              <SummaryTile testid="repair-would-fix" label="Would repair" value={preview.updated} accent="emerald" />
              <SummaryTile testid="repair-skipped" label="No source"  value={preview.skipped_no_source} accent="amber" />
            </div>

            {preview.updated === 0 ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-start gap-2" data-testid="pws-repair-names-clean">
                <CheckCircle2 className="w-4 h-4 mt-0.5" />
                Every partner already has a business name — nothing to repair.
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 overflow-hidden" data-testid="pws-repair-names-preview-list">
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-2 font-bold">Email</th>
                        <th className="text-left px-3 py-2 font-bold">Resolved name</th>
                        <th className="text-left px-3 py-2 font-bold">Source</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {preview.results.map((r) => (
                        <tr key={r.partner_id} data-testid={`pws-repair-row-${r.partner_id}`}>
                          <td className="px-3 py-2 text-slate-500 truncate max-w-[180px]">{r.email || "—"}</td>
                          <td className="px-3 py-2 font-semibold text-slate-800">{r.resolved_name}</td>
                          <td className="px-3 py-2 text-slate-500">
                            <code className="text-[10px]">{r.source}</code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="pws-repair-names-cancel"
            className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900"
          >
            Close
          </button>
          {phase === "preview" && preview?.updated > 0 && (
            <button
              onClick={apply}
              data-testid="pws-repair-names-apply"
              className="px-4 py-1.5 rounded-full text-xs font-bold bg-cyan-600 hover:bg-cyan-700 text-white inline-flex items-center gap-1.5"
            >
              <Wrench className="w-3.5 h-3.5" />
              Apply {preview.updated} repair{preview.updated === 1 ? "" : "s"}
            </button>
          )}
          {phase === "applying" && (
            <button
              disabled
              className="px-4 py-1.5 rounded-full text-xs font-bold bg-cyan-400 text-white inline-flex items-center gap-1.5"
              data-testid="pws-repair-names-applying"
            >
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Applying…
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
